import { useCallback } from 'react';
import SplitToggleButtonWithPopper from '../../../../../../../components/buttons/SplitButtonWithPopper';
import LangEnum from '../../../../../../../lang/LangEnum';
import { ProductDetailsFilterDeliveryWindow } from '../../../../../../../services/modules/productDetails/productDetails.types';
import { getAvailabilityLabel } from '../../../../../../../utils/getAvailabilityLabel';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { getAvailabilityLangEnum } from '../../../../../ProductAvailabilityRenderer';
import { CompactAvailability } from '../../../../../ProductTypes';
import { useProductFiltersStyles } from '../../ProductFilters/styles/useProductFilters.styles';
import DeliveryWindowsMenuContent from '../DeliveryWindowMenuContent';
import ProductFilterToggleButton from '../button/components/ProductFilterToggleButton';
import { useAvailabilityFilterState } from './hooks/useAvailabilityFilter';

interface AvailabilityFilterProps {
  deliveryWindows: ProductDetailsFilterDeliveryWindow[];
  availabilities: CompactAvailability[];
}

const AvailabilityFilter = ({ deliveryWindows, availabilities }: AvailabilityFilterProps) => {
  const { translate } = useTranslation();

  const { classes, cx } = useProductFiltersStyles();
  const {
    isPreOrderSelected,
    selectedDeliveryWindows,
    productGridParamsDeliveryWindowIds,
    isReadyForDeliverySelected,
    isAllAvailabilitiesSelected,
    deliveryWindowsForDropdown,
    isPreOrderDisabled,
    isReadyForDeliveryDisabled,
    toggleWindow,
    updateAvailability,
    applyDeliveryWindows,
    handleToggleAllWindows,
  } = useAvailabilityFilterState({ deliveryWindows, availabilities });

  const commonClasses = [
    classes.filterButton,
    classes.splitButtonTextStyle,
    classes.selectedFilterButton,
  ];

  const getPreOrderFilterLabel = useCallback(() => {
    if (
      productGridParamsDeliveryWindowIds.length &&
      productGridParamsDeliveryWindowIds.length > 1
    ) {
      return translate(LangEnum.GLOBAL_DELIVERIES_LABEL_WITH_COUNT, {
        count: productGridParamsDeliveryWindowIds.length,
      });
    }

    if (productGridParamsDeliveryWindowIds.length === 1) {
      const selectedDeliveryWindow = deliveryWindowsForDropdown.find(
        (dw) => dw.id === productGridParamsDeliveryWindowIds[0],
      );
      return getAvailabilityLabel({
        availability: CompactAvailability.PRE_ORDER,
        deliveryWindowName: selectedDeliveryWindow?.name || null,
        translate,
      });
    }

    return translate(getAvailabilityLangEnum()[CompactAvailability.PRE_ORDER]);
  }, [productGridParamsDeliveryWindowIds, deliveryWindowsForDropdown, translate]);

  return (
    <>
      <SplitToggleButtonWithPopper
        buttonValue={CompactAvailability.PRE_ORDER}
        buttonLabel={getPreOrderFilterLabel()}
        onClickMainButton={() => updateAvailability(CompactAvailability.PRE_ORDER)}
        onCloseDropdown={applyDeliveryWindows}
        selectedValue={
          isAllAvailabilitiesSelected || isPreOrderSelected ? CompactAvailability.PRE_ORDER : ''
        }
        disabled={isPreOrderDisabled}
        className={cx(
          ...commonClasses,
          isPreOrderDisabled &&
            (isAllAvailabilitiesSelected || isPreOrderSelected) &&
            classes.disabledSelectedFilterButton,
        )}
        iconButtonClassName={cx(
          ...commonClasses,
          classes.iconDropdownButton,
          isPreOrderDisabled &&
            (isAllAvailabilitiesSelected || isPreOrderSelected) &&
            classes.disabledSelectedFilterButton,
        )}
        iconSize={{ width: 14, height: 14 }}
        menuContent={
          <DeliveryWindowsMenuContent
            deliveryWindows={deliveryWindowsForDropdown}
            localSelectedDeliveryWindows={selectedDeliveryWindows}
            onToggleDeliveryWindow={toggleWindow}
            handleToggleAllWindows={handleToggleAllWindows}
          />
        }
        menuProps={{
          PaperProps: {
            elevation: 0,
            className: classes.deliveryWindowsMenuPaper,
          },
        }}
      />

      <ProductFilterToggleButton
        selected={isAllAvailabilitiesSelected || isReadyForDeliverySelected}
        label={getAvailabilityLangEnum(true)[CompactAvailability.READY_FOR_DELIVERY]}
        value={CompactAvailability.READY_FOR_DELIVERY}
        disabled={isReadyForDeliveryDisabled}
        className={cx(
          classes.filterButton,
          (isAllAvailabilitiesSelected || isReadyForDeliverySelected) &&
            classes.selectedFilterButton,
          isReadyForDeliveryDisabled &&
            (isAllAvailabilitiesSelected || isReadyForDeliverySelected) &&
            classes.disabledSelectedFilterButton,
        )}
        onClick={() => updateAvailability(CompactAvailability.READY_FOR_DELIVERY)}
      />
    </>
  );
};

export default AvailabilityFilter;
